import React, { useMemo, useState } from 'react';
import { Dimensions, useWindowDimensions, View } from 'react-native';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import colors from '../../../config/colors';
import I18n from '../../../components/shared/i18n/I18n.component';
import Header from '../../../components/shared/Header/Header.container';
import ReportsPageWrapper from '../../../components/web/ReportsPageWrapper/ReportsPageWrapper.component';
import { i18nService } from '../../../services/i18n.service';
import Filters from '../../../components/web/Filters/Filters.component';
import TableContainer from '../../../components/web/ReactTable/Table.container';
import { useSelector } from 'react-redux';
import { getEmployerRolesPrivileges } from '../../../store/selectors/login.selectors';
import { Privileges } from '../../../constants/roleTypes.constant';
import WebHeader from '../../../components/shared/Header/WebHeader.component';
import {
	onDelete,
	onEdit,
} from './LocationSettingsPage.utils';
import { modalService } from '../../../services/modal.service';
import LocationSettingsModal from './LocationSettingsModal/LocationSettingsModal';
import httpService from '../../../services/http.service';
import SolidButtonContainer from '../../../components/shared/Buttons/SolidButton.container';
import ExistingLocationSettingsModal from './ExistingLocationSettingsModal/ExistingLocationSettingsModal';
import Typography from '../../../components/shared/Typography/Typography.component';
import { hasPermissionOnBusinessUnit } from '../../../utils/globals.utils';
import { compact } from 'lodash';
import ButtonV2 from '../../../components/shared/Buttons/ButtonV2.container';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';

export interface Props {
	navigation: any;
	data: any[];
	projectsFilterList: any[];
	departmentsFilterList: any[];
	filtersConfig: any;
	onSort: (field: string, direction: string) => void;
	onLoadMore(): void;
	onFilter(filterData: any): void;
	refresh(): void;
	businessUnitsList: any[];
	selectedUnitIds: any[];
}

function ActionsCell({ onEdit, onDelete, canDelete }: any) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};
	const handleClose = () => setAnchorEl(null);

	return (
		<div
			className='edit-cell-icon'
			style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}
		>
			<div
				onClick={handleOpen}
				style={{ cursor: 'pointer', width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center' }}
			>
				<ImageIcon name='verticalDots' width={4} height={22} />
			</div>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				PaperProps={{ style: { borderRadius: 8, minWidth: 180 } }}
			>
				<MenuItem
					onClick={() => {
						onEdit();
						handleClose();
					}}
					style={{ borderBottom: canDelete ? '1px solid #E2EDF4' : undefined, justifyContent: 'flex-end', gap: 16, paddingLeft: 16, paddingRight: 16, paddingTop: 10, paddingBottom: 10 }}
				>
					<I18n color='primaryDarkBlue'>{i18nService.translate('EmployeeHoursReport.table.edit')}</I18n>
					<ImageIcon name='editPencil' width={18} height={18} />
				</MenuItem>
				{canDelete && (
					<MenuItem
						onClick={() => {
							onDelete();
							handleClose();
						}}
						style={{ justifyContent: 'flex-end', gap: 16, paddingLeft: 16, paddingRight: 16, paddingTop: 10, paddingBottom: 10 }}
					>
						<I18n color='primaryDarkBlue'>{i18nService.translate('general.delete')}</I18n>
						<ImageIcon name='trash' width={18} height={18} />
					</MenuItem>
				)}
			</Menu>
		</div>
	);
}

const LocationSettingsPage = ({
	navigation,
	data,
	filtersConfig,
	projectsFilterList,
	departmentsFilterList,
	onSort,
	onLoadMore,
	onFilter,
	refresh,
	businessUnitsList,
	selectedUnitIds,
}: Props) => {
	const [getUsersByDepartments, setGetUsersByDepartments] = useState([]);
	const { height: windowHeight } = useWindowDimensions();

	const headers = () => {
		const firstList = compact([
			{
				accessor: 'locationName',
				Header: i18nService.translate('locationSettings.table.address'),
				isSortable: true,
			},
			{
				accessor: 'name',
				Header: i18nService.translate(
					'locationSettings.table.addressName',
				),
				isSortable: true,
			},
			{
				accessor: 'radius',
				Header: i18nService.translate('locationSettings.table.radius'),
				isSortable: true,
				style: { width: 80 },
				CustomCell: ({ value }: any) => (
					<Typography color='clockRecordBlue' size={14}>
						{value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
					</Typography>
				),
			},
			businessUnitsList.length > 1
				? {
						accessor: 'businessUnitName',
						Header: i18nService.translate('general.businessUnits'),
						isSortable: true,
				  }
				: null,
		]);

		const projectList = projectsFilterList.length
			? [
					{
						accessor: 'projects',
						Header: i18nService.translate(
							'locationSettings.table.projects',
						),
						isSortable: false,
						CustomCell: ({ value }: any) => {
							if (!!value) {
								const names = value.split(',');
								return (
									<>
										<I18n color='clockRecordBlue' size={14}>
											{`${
												names.length > 2
													? `${names[0]}, ${names[1]}`
													: value
											}`}
										</I18n>
										{names.length > 2 && (
											<I18n color='clockRecordBlue' size={14}>
												{`\n+${names.length - 2}`}
											</I18n>
										)}
									</>
								);
							} else return <View />;
						},
					},
			  ]
			: [];

		const secondList = [
			{
				accessor: 'accessFor',
				Header: i18nService.translate('locationSettings.table.belongs'),
				isSortable: false,
				CustomCell: ({ value }: any) => {
					const departments = value?.departments;
					const users = value?.users;
					if (!!users || !!departments) {
						const namesArr = users?.split(',') || [];
						const departmentsArr = departments?.split(',') || [];
						let text = '';
						let number = namesArr.length + departmentsArr.length;
						if (!!users && !!departments) {
							text = `${departmentsArr[0]}, ${namesArr[0]}`;
							number = number - 2;
						} else {
							text = `${departmentsArr[0] || namesArr[0]}${
								number > 1
									? `, ${departmentsArr[1] || namesArr[1]}`
									: ''
							}`;
							number = number > 2 ? number - 2 : 0;
						}
						return (
							<>
								<I18n color='clockRecordBlue' size={14} numberOfLines={1}>{text}</I18n>
								{number > 0 && (
									<I18n color='clockRecordBlue' size={14} numberOfLines={1}>{`+${number} ,`}</I18n>
								)}
							</>
						);
					} else return <View />;
				},
			},
			{
				accessor: 'status',
				Header: i18nService.translate('locationSettings.table.status'),
				isSortable: true,
				style: { width: 60 },
				CustomCell: ({ value }: any) => (
					<I18n color='clockRecordBlue' size={14}>
						{value ? 'general.active' : 'general.inactive'}
					</I18n>
				),
			},
			{
				accessor: 'actions',
				Header: '',
				isSortable: false,
				style: { width: 40 },
				CustomCell: ({ row }: any) => {
					const canDelete = hasPermissionOnBusinessUnit(
						row.original.businessUnitId,
						Privileges.ROLE_LOCATION_WRITE,
					);
					return (
						<ActionsCell
							onEdit={() =>
								onEdit(
									row.original.id,
									projectsFilterList,
									departmentsFilterList,
									refresh,
									row.original.businessUnitId,
								)
							}
							onDelete={() => onDelete(row.original.id, refresh)}
							canDelete={canDelete}
						/>
					);
				},
			},
		];

		return [...firstList, ...projectList, ...secondList];
	};

	const updateFilters = val => {
		onFilter(val);
	};

	const onAddNewLocation = async () => {
		let userList = [];
		const allowedBusinessUnitsList = businessUnitsList.filter(unit =>
			hasPermissionOnBusinessUnit(
				unit.id,
				Privileges.ROLE_LOCATION_WRITE,
			),
		);
		const allowedSelectedUnitIds = selectedUnitIds.filter(unit =>
			hasPermissionOnBusinessUnit(unit, Privileges.ROLE_LOCATION_WRITE),
		);
		if (
			!getUsersByDepartments.length &&
			allowedSelectedUnitIds?.length === 1
		) {
			const res = await httpService.api<any>({
				type: 'getUsersByDepartments',
				query: {
					pageName: 'locationSettings',
					unitId: allowedSelectedUnitIds?.[0],
				},
				data: {
					search: '',
					f: { dep: [], internal: ['true'] },
				},
			});
			userList = res;
			setGetUsersByDepartments(res);
		} else {
			userList = getUsersByDepartments;
		}

		await modalService.openModal(null, {hideCloseBtn: true}, (props: any) => (
			<LocationSettingsModal
				{...props}
				refresh={refresh}
				users={userList}
				unitIds={allowedSelectedUnitIds}
				businessUnitsList={allowedBusinessUnitsList}
				projectsFilterList={projectsFilterList}
				departmentsFilterList={departmentsFilterList}
				onClose={() => props.onNext({ type: 'close' })}
			/>
		));
	};

	const onUpdateExistingLocation = async () => {
		const allowedBusinessUnitsList = businessUnitsList.filter(
			unit =>
				hasPermissionOnBusinessUnit(
					unit.id,
					Privileges.ROLE_LOCATION_WRITE,
				) ||
				hasPermissionOnBusinessUnit(
					unit.id,
					Privileges.ROLE_LOCATION_ASSOCIATION_WRITE,
				),
		);
		const allowedSelectedUnitIds = selectedUnitIds.filter(
			unit =>
				hasPermissionOnBusinessUnit(
					unit,
					Privileges.ROLE_LOCATION_WRITE,
				) ||
				hasPermissionOnBusinessUnit(
					unit,
					Privileges.ROLE_LOCATION_ASSOCIATION_WRITE,
				),
		);

		await modalService.openModal(null, {hideCloseBtn: true}, (props: any) => (
			<ExistingLocationSettingsModal
				{...props}
				onClose={props.onNext}
				refresh={refresh}
				locationsList={[]}
				projectsList={projectsFilterList}
				unitIds={allowedSelectedUnitIds}
				businessUnitsList={allowedBusinessUnitsList}
			/>
		));
	};
	const disabled = useMemo(
		() =>
			selectedUnitIds?.some(unit =>
				hasPermissionOnBusinessUnit(
					unit,
					Privileges.ROLE_LOCATION_ASSOCIATION_WRITE,
				),
			)
				? false
				: true,
		[selectedUnitIds],
	);

	const createDisabled = useMemo(
		() =>
			selectedUnitIds?.some(unit =>
				hasPermissionOnBusinessUnit(
					unit,
					Privileges.ROLE_LOCATION_WRITE,
				),
			)
				? false
				: true,
		[selectedUnitIds],
	);

	return (
		<View
			style={{
				height: '100%',
			}}
		>
			<Header
				navigation={navigation}
				containerStyle={{ backgroundColor: colors.white }}
				component={WebHeader}
			/>
			<ReportsPageWrapper
				navigation={navigation}
				title={
					<View style={{ width: '100%', flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center' }}>
						<View style={{ flexDirection: 'row-reverse', alignItems: 'center' }}>
							<ImageIcon name="clockInLogo" width={108} height={30} style={{marginLeft: 12}} />
							<I18n
							size={20}
							weight='bold'
							color='primaryDarkBlue'
							style={{ flex: 1 }}
							>
								locationSettings.title
							</I18n>
						</View>

						<View style={{ flexDirection: 'row-reverse' }}>
							{data.length
								? (!createDisabled || !disabled) && (
										<ButtonV2 preset='outline' onPress={onUpdateExistingLocation} overrideStyle={{width: 'auto'}}>
											<I18n color='titlePrimary'>
												{`${i18nService.translate(
													'existingLocationSettingsModal.title',
												)} ${
													projectsFilterList.length
														? i18nService.translate(
																'existingLocationSettingsModal.titleOrProject',
														)
														: ''
												}`}
											</I18n>
											<ImageIcon name='checkBox' width={18} height={18} color={colors.titlePrimary} style={{ marginLeft: 8, marginTop: 1 }} />
										</ButtonV2>
									)
								: null}
							{!createDisabled && (
								<ButtonV2
									preset='primary'
									onPress={onAddNewLocation}
									overrideStyle={{
										width: 'auto',
										marginRight: 10,
									}}
								>
									<Typography
										color='white'
										weight='400'
										size={14}
									>
										{i18nService.translate('locationSettings.modalTitle.newLocationBtn')}
									</Typography>
									<ImageIcon
										name='plus'
										color='white'
										style={{ marginLeft: 8 }}
										width={12}
										height={12}
									/>
								</ButtonV2>
							)}
						</View>
					</View>
				}
				noHeader={true}
			>
				<View
					style={{
						flexDirection: 'row-reverse',
						justifyContent: 'space-between',
						zIndex: 100,
					}}
				>
					<Filters
						config={filtersConfig}
						onChange={updateFilters}
						customStyles={{
							container: {
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-end',
								marginBottom: 24,
							},
							filterContainer: {
								marginLeft: 15,
								zIndex: 10001,
							},
						}}
					/>
					
				</View>
				<View
					style={{
						flex: 1,
						maxHeight: windowHeight - (!createDisabled ? 235 : 255),
						marginTop: !createDisabled ? 0 : 20,
					}}
				>
					{data.length ? (
						<TableContainer
							keyName={''}
							headers={headers()}
							data={data || []}
							getNextData={onLoadMore}
							onSort={onSort}
							hasFooter={false}
							selectedRowId={''}
						/>
					) : (
						<View
							style={{
								flex: 1,
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<I18n
								weight='bold'
								size={24}
								style={{ marginRight: 26, marginTop: 50 }}
							>
								locationSettings.noData
							</I18n>
						</View>
					)}
				</View>
			</ReportsPageWrapper>
		</View>
	);
};

export default LocationSettingsPage;
